import React, { useEffect, useState } from 'react'
import { systemContext } from './context'
import { useDispatch, useSelector } from 'react-redux'
import { fallback, fetchSystemList } from './slice'
import { AlertError, Loader } from '../../components'

const SystemProvider = ({ children }) => {
  const dispatch = useDispatch()
  const language = useSelector(state => state.system.language)
  const status = useSelector(state => state.system.status)
  const error = useSelector(state => state.system.error)
  const [errorMessage, setErrorMessage] = useState(null)
  const approaches = useSelector(state => state.system.approaches)
  const issues = useSelector(state => state.system.issues)
  const organizationTypes = useSelector(state => state.system.organizationTypes)
  const audiences = useSelector(state => state.system.audiences)
  const categoriesResource = useSelector(state => state.system.categories_resource)
  const categoriesBlog = useSelector(state => state.system.categories_blog)
  const typeResources = useSelector(state => state.system.typeResources)

  const [approachOptions, setApproachOptions] = useState([])
  const [issueOptions, setIssueOptions] = useState([])
  const [organizationTypeOptions, setOrganizationTypeOptions] = useState([])
  const [audienceOptions, setAudienceOptions] = useState([])
  const [uppercasedAudienceOptions, setUppercasedAudienceOptions] = useState([])
  const [uppercasedAudienceOptionsMap, setUppercasedAudienceOptionsMap] = useState([])
  const [categoryResourceOptions, setCategoryResourceOptions] = useState([])
  const [categoryBlogOptions, setCategoryBlogOptions] = useState([])
  const [typeResourceOptions, setTypeResourceOptions] = useState([])

  useEffect(() => {
    if(status === 'idle'){
      dispatch(fetchSystemList())
    }
  }, [status, dispatch])

  useEffect(() => {
    if(error) setErrorMessage(error)
  }, [error])

  const formatter = React.useCallback(
    (data) => ({value: data.id, label: data.name[language.name]}),
    [language],
  );

  const uppercaseFormatter = React.useCallback(
    (data) => {
      let result = []
      let map = {}

      for (let index = 0; index < data.length; index++) {
        const element = data[index];
        const uppercased = element.name[language.name].toUpperCase()
        result.push(uppercased)
        map[uppercased] = element
      }

      return {data: result, map}
    },
    [language]
  )

  useEffect(() => {
    if(status === 'succeeded'){
      setApproachOptions(approaches.map(a => formatter(a)))
      setIssueOptions(issues.map(a => formatter(a)))
      setOrganizationTypeOptions(organizationTypes.map(a => formatter(a)))
      setAudienceOptions(audiences.map(a => formatter(a)))
      setUppercasedAudienceOptions(uppercaseFormatter(audiences).data)
      setUppercasedAudienceOptionsMap(uppercaseFormatter(audiences).map)
      setCategoryResourceOptions(categoriesResource.map(a => formatter(a)))
      setCategoryBlogOptions(categoriesBlog.map(a => formatter(a)))
      setTypeResourceOptions(typeResources.map(a => formatter(a)))
    }
  }, [status, language, approaches, issues, organizationTypes, audiences, categoriesBlog, categoriesResource, typeResources, formatter, uppercaseFormatter])

  return (
    <systemContext.Provider value={{
      approaches,
      issues,
      organizationTypes,
      audiences,
      approachOptions,
      issueOptions,
      organizationTypeOptions,
      audienceOptions,
      categoryResourceOptions,
      categoryBlogOptions,
      typeResourceOptions,
      uppercasedAudienceOptions,
      uppercasedAudienceOptionsMap
    }}>
      {
        status === 'idle' &&
        <div className="text-center py-5 text-sm">
          Some data need to be fetched. <span className="link cursor-pointer" onClick={() => dispatch(fetchSystemList())}>Fetch</span>
        </div>
      }
      {
        status === 'loading' &&
        <div className="text-center py-5 text-sm">
          <Loader />
        </div>
      }
      {
        status === 'failed' &&
        <div className="text-center py-5 text-sm">
          <div className="mb-4">
            <p>Some data failed to be fetched.</p>
            <div>
              <span className="link cursor-pointer mr-4" onClick={() => dispatch(fetchSystemList())}>
                Retry
              </span>
              <span className="link cursor-pointer" onClick={() => dispatch(fallback())}>
                Fallback
              </span>
            </div>
          </div>
          {
            errorMessage !== null &&
            <AlertError message={error} onClose={() => setErrorMessage(null)}/>
          }
        </div>
      }
      {
        status === 'succeeded' &&
        children
      }
    </systemContext.Provider>
  );
};

export { SystemProvider }
